'use client';

import { cn } from '@/lib/utils';
import { CHAT_MODELS, type ChatModelId } from '@/lib/ai/chat-models';

/**
 * »Made in Slovenia« badge za GaMS (ali custom) model.
 *
 * - `variant="header"`: stalno viden ob wordmarku, ko je izbran slovenski model.
 * - `variant="message"`: drobna provenienca ob AI odgovoru (iz message.model).
 *
 * Zastavica je emoji 🇸🇮 (brez zunanjih assetov — CSP-varno).
 */
export function ModelBadge({
  modelId,
  variant = 'message',
  className,
}: {
  modelId: ChatModelId | undefined;
  variant?: 'header' | 'message';
  className?: string;
}) {
  if (!modelId) return null;
  const meta = CHAT_MODELS[modelId];
  if (!meta || !meta.slovenian) return null;

  if (variant === 'header') {
    return (
      <span
        title={`Poganja slovenski jezikovni model: ${meta.label}`}
        className={cn(
          'inline-flex items-center gap-1 rounded-full border border-primary/30',
          'bg-primary/5 px-2 py-0.5 text-[10px] font-semibold tracking-wide',
          'text-primary',
          className
        )}
      >
        <span aria-hidden className="text-xs leading-none">🇸🇮</span>
        <span>{meta.shortLabel}</span>
        <span className="hidden sm:inline text-muted-foreground font-normal">· Made in Slovenia</span>
      </span>
    );
  }

  return (
    <span
      title={`Odgovor generiral slovenski model: ${meta.label}`}
      className={cn(
        'inline-flex items-center gap-0.5 text-[8px] font-semibold uppercase tracking-wider',
        'text-primary/80 leading-none',
        className
      )}
    >
      <span aria-hidden className="text-[10px] leading-none">🇸🇮</span>
      {meta.shortLabel}
    </span>
  );
}
